import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, Button, Flex, Pagination } from '@decisiv/ui-components';
import { H1, P } from '@decisiv/ui-components/lib/components/Typography';
import Table from '@decisiv/ui-components/lib/components/Table';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import { t, Trans } from '@lingui/macro';

import Helmet from '../../components/SEO/Helmet';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import SharedSettingsApi from '../../api/shared_settings';
import localCache from '../../utils/localCache';
import { STORAGE_KEYS } from '../../utils/storageKeys';
import {
  NeedsBody,
  NeedsCard,
  NeedsCardHead,
  NeedsControls,
  NeedsCount,
  NeedsEmpty,
  NeedsHeader,
  NeedsPage,
  ViewButton,
  ViewSwitch,
} from './style';

const PAGE_SIZE = 25;
const GROUPED = 'grouped';
const FULL = 'full';

const locationPath = (row) =>
  `/location_settings/${row.attributes.slug || row.id}`;

const LocationCell = (data) => (
  <Link to={locationPath(data.rowData)}>
    {data.rowData.attributes.name || data.rowData.attributes.srm_account_id}
  </Link>
);

const SrmAccountCell = (data) => (
  <span>{data.rowData.attributes.srm_account_id}</span>
);

const StatusCell = (data) => (
  <Tag
    text={data.rowData.attributes.enabled ? t`Enabled` : t`Disabled`}
    color={data.rowData.attributes.enabled ? 'success' : 'licoriceMousse'}
  />
);

const MissingCell = (data) => (
  <span>{(data.rowData.attributes.missing_attributes || []).join(', ')}</span>
);

// Every location of the group that no integration can enable as it stands, each
// row linking to where it gets fixed. The same location appears once per
// integration that cannot enable it, hence the grouped default view.
export default function NeedsAttentionPage() {
  const { uuid } = useParams();

  const [rows, setRows] = useState([]);
  const [integrationNames, setIntegrationNames] = useState({});
  const [groupName, setGroupName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [view, setView] = useState(() => {
    const stored = localCache.get(STORAGE_KEYS.NEEDS_ATTENTION_VIEW);
    return [GROUPED, FULL].includes(stored) ? stored : GROUPED;
  });

  const load = useCallback(async () => {
    setLoading(true);
    const { data, sharedSettingName, errors } =
      await SharedSettingsApi.fetchNeedsAttention(uuid);

    // An empty list and a failed request are not the same thing: reporting a
    // clean group during an outage is worse than saying nothing.
    setLoadFailed(!!errors || !data);
    setRows(data || []);
    if (sharedSettingName) setGroupName(sharedSettingName);
    setLoading(false);
  }, [uuid]);

  useEffect(() => {
    load();
  }, [load]);

  // The group itself is what names its integrations — the rows carry only the
  // uuid. Fetched once, not per page of results.
  useEffect(() => {
    async function loadIntegrationNames() {
      const { data } = await SharedSettingsApi.fetchSharedSetting(uuid);
      if (!data) return;

      setIntegrationNames(
        Object.fromEntries(
          (data.attributes?.integrations || []).map((i) => [i.uuid, i.name]),
        ),
      );
    }

    loadIntegrationNames();
  }, [uuid]);

  const selectView = (next) => {
    setView(next);
    setCurrentPage(1);
    localCache.set(STORAGE_KEYS.NEEDS_ATTENTION_VIEW, next);
  };

  const nameFor = useCallback(
    (integrationUuid) => integrationNames[integrationUuid] || integrationUuid,
    [integrationNames],
  );

  const baseColumns = useMemo(
    () => [
      { DataCell: LocationCell, title: t`LOCATION`, name: 'location' },
      {
        DataCell: SrmAccountCell,
        title: t`SRM ACCOUNT`,
        name: 'srm_account_id',
      },
      { DataCell: StatusCell, title: t`STATUS`, name: 'status' },
      { DataCell: MissingCell, title: t`MISSING`, name: 'missing' },
    ],
    [],
  );

  const groups = useMemo(() => {
    const byIntegration = rows.reduce((acc, row) => {
      const key = row.attributes.integration_uuid;
      (acc[key] = acc[key] || []).push(row);
      return acc;
    }, {});

    return Object.entries(byIntegration)
      .map(([integrationUuid, groupRows]) => ({
        integrationUuid,
        name: nameFor(integrationUuid),
        rows: groupRows,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, nameFor]);

  const fullColumns = useMemo(
    () => [
      {
        DataCell: (data) => (
          <span>{nameFor(data.rowData.attributes.integration_uuid)}</span>
        ),
        title: t`INTEGRATION`,
        name: 'integration',
      },
      ...baseColumns,
    ],
    [nameFor, baseColumns],
  );

  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pagedRows = rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  return (
    <NeedsPage>
      <Helmet
        title={t`Needs Attention`}
        description={t`Locations with an incomplete configuration in this Shared Setting`}
      />

      <NeedsHeader>
        <Breadcrumbs
          path={[
            { to: '/toolkit/shared_settings', text: 'Shared Settings' },
            ...(groupName
              ? [{ to: `/toolkit/shared_settings/${uuid}`, text: groupName }]
              : []),
          ]}
          current={t`Needs Attention`}
        />
        <H1 marginTop={1}>
          <Trans>Needs Attention</Trans>
        </H1>
        <P shade={1} marginTop={0.5} marginBottom={0}>
          <Trans>
            These locations are missing a value their integration marks as
            required, so the Shared Setting cannot enable them. Open a location
            to fill in what is missing.
          </Trans>
        </P>
      </NeedsHeader>

      <NeedsBody>
        <NeedsControls>
          <NeedsCount>
            {!loadFailed && rows.length > 0 && (
              <Trans>{rows.length} in total</Trans>
            )}
          </NeedsCount>
          <ViewSwitch role="group" aria-label={t`Choose view`}>
            <ViewButton
              type="button"
              aria-pressed={view === GROUPED}
              onClick={() => selectView(GROUPED)}
            >
              <Trans>Per Integration</Trans>
            </ViewButton>
            <ViewButton
              type="button"
              aria-pressed={view === FULL}
              onClick={() => selectView(FULL)}
            >
              <Trans>Full</Trans>
            </ViewButton>
          </ViewSwitch>
        </NeedsControls>

        <WrapperWithLoading loading={loading}>
          {loadFailed && (
            <NeedsCard>
              <NeedsEmpty>
                <P marginBottom={1}>
                  <Trans>
                    We could not load this list. Nothing here means the request
                    failed, not that the group is clean.
                  </Trans>
                </P>
                <Button text={t`Try again`} kind="secondary" onClick={load} />
              </NeedsEmpty>
            </NeedsCard>
          )}

          {!loadFailed && rows.length === 0 && (
            <NeedsCard>
              <NeedsEmpty>
                <div className="mark">✓</div>
                <P shade={1} marginBottom={0}>
                  <Trans>
                    Nothing needs attention in this Shared Setting right now.
                  </Trans>
                </P>
              </NeedsEmpty>
            </NeedsCard>
          )}

          {!loadFailed && rows.length > 0 && (
            <>
              {view === GROUPED &&
                groups.map((group) => (
                  <NeedsCard key={group.integrationUuid}>
                    <NeedsCardHead>
                      <span className="name">{group.name}</span>
                      <span className="count">
                        <Trans>{group.rows.length} to resolve</Trans>
                      </span>
                    </NeedsCardHead>
                    <Table
                      columns={baseColumns}
                      data={group.rows}
                      kind="secondary"
                      getRowKey={({ id }) => id}
                    />
                  </NeedsCard>
                ))}

              {view === FULL && (
                <>
                  <NeedsCard>
                    <Table
                      columns={fullColumns}
                      data={pagedRows}
                      kind="secondary"
                      getRowKey={({ id }) => id}
                    />
                  </NeedsCard>
                  {totalPages > 1 && (
                    <Flex justifyContent="flex-end">
                      <Pagination
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        activePage={currentPage}
                      />
                    </Flex>
                  )}
                </>
              )}
            </>
          )}
        </WrapperWithLoading>
      </NeedsBody>
    </NeedsPage>
  );
}
